import { useState } from 'react';
import { Panel } from '@/components/Panel';
import { usePendingReview } from '@/hooks/usePendingReview';
import { useAuth } from '@/hooks/useAuth';
import { Spinner, EmptyState, StatusBadge, Button, ConfirmDialog } from '@00start/ui';
import { formatDuration } from '@/utils/chart-helpers';
import { postJSON } from '@/api/client';

function resolutionTime(opened_at: string | number, resolved_at: string | number | null): string {
  if (!resolved_at) return '-';
  const elapsed = Number(resolved_at) - Number(opened_at);
  if (isNaN(elapsed) || elapsed < 0) return '-';
  return formatDuration(elapsed);
}

function severityVariant(severity: string): 'critical' | 'warning' | 'neutral' {
  if (severity === 'P0' || severity === 'P1') return 'critical';
  if (severity === 'P2') return 'warning';
  return 'neutral';
}

interface RejectState {
  incidentId: string;
  open: boolean;
}

export function PendingReview() {
  const { incidents, loading } = usePendingReview();
  const { token } = useAuth();
  const [acting, setActing] = useState<string | null>(null);
  const [rejectState, setRejectState] = useState<RejectState>({ incidentId: '', open: false });

  async function handleApprove(id: string) {
    setActing(id);
    try {
      await postJSON(`/api/incidents/${id}/close`, token, {});
      window.location.reload();
    } finally {
      setActing(null);
    }
  }

  function openReject(id: string) {
    setRejectState({ incidentId: id, open: true });
  }

  function closeReject() {
    setRejectState({ incidentId: '', open: false });
  }

  async function handleRejectConfirm() {
    const id = rejectState.incidentId;
    closeReject();
    setActing(id);
    try {
      await postJSON(`/api/incidents/${id}/rca/reject`, token, {});
      window.location.reload();
    } finally {
      setActing(null);
    }
  }

  return (
    <Panel title="Pending Review" mobilePriority={7}>
      {loading ? (
        <div className="flex justify-center py-4"><Spinner size="sm" /></div>
      ) : !incidents || incidents.length === 0 ? (
        <EmptyState title="No incidents pending review" description="All incidents have been reviewed." />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-slate-500 border-b border-slate-800">
                  <th className="text-left py-2 pr-3">Sev</th>
                  <th className="text-left py-2 pr-3">ID</th>
                  <th className="text-left py-2 pr-3">Opened</th>
                  <th className="text-left py-2 pr-3">Resolution Time</th>
                  <th className="text-left py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {incidents.map((inc) => (
                  <tr key={inc.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                    <td className="py-2 pr-3">
                      <StatusBadge status={inc.severity} variant={severityVariant(inc.severity)} />
                    </td>
                    <td className="py-2 pr-3 text-slate-300 font-mono">{inc.id}</td>
                    <td className="py-2 pr-3 text-slate-400">
                      {new Date(inc.opened_at).toLocaleString()}
                    </td>
                    <td className="py-2 pr-3 text-slate-400 tabular-nums">
                      {resolutionTime(inc.opened_at, inc.resolved_at)}
                    </td>
                    <td className="py-2">
                      <div className="flex gap-2">
                        <Button
                          data-testid="ops-pending-action-approve"
                          variant="primary"
                          size="sm"
                          disabled={acting === inc.id}
                          onClick={() => handleApprove(inc.id)}
                        >
                          Approve
                        </Button>
                        <Button
                          data-testid="ops-pending-action-reject"
                          variant="danger"
                          size="sm"
                          disabled={acting === inc.id}
                          onClick={() => openReject(inc.id)}
                        >
                          Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ConfirmDialog
            isOpen={rejectState.open}
            onClose={closeReject}
            onConfirm={handleRejectConfirm}
            title="Reject RCA"
            description="Confirm rejection of this incident's RCA. This will mark the incident as requiring further review."
            confirmLabel="Reject"
            confirmVariant="danger"
          />
        </>
      )}
    </Panel>
  );
}
